import { useMemo } from 'react';
import { css } from '@emotion/react';
import { PartnerInfo } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/integration.pb';
import dayjs from 'dayjs';
import { Accordion, Text } from '@frontend/design-system';
import { UserRole } from '../../utils/constants';
import { checkRbacAccess } from '../../utils/helpers';
import { IntegrationsMetadata } from '../../utils/typings';

interface PartnerInformationAccordionProps {
  partnerInformation?: PartnerInfo;
  integrationsMetadata?: IntegrationsMetadata;
  userRole?: keyof typeof UserRole;
}

const PartnerInformationAccordion = ({
  partnerInformation,
  integrationsMetadata,
  userRole,
}: PartnerInformationAccordionProps) => {
  const hasViewAccess = useMemo(
    () => checkRbacAccess(integrationsMetadata?.partnerInformation, userRole).hasViewAccess,
    [integrationsMetadata, userRole]
  );

  const infoFields = useMemo(() => {
    const authorizedSince = partnerInformation?.authorizedIntegrationSince
      ? dayjs(partnerInformation.authorizedIntegrationSince).format('LLL')
      : undefined;

    return [
      ['Parent Company', partnerInformation?.parentCompany],
      ['Partner Status', partnerInformation?.partnerStatus],
      ['Partner Integration Status', partnerInformation?.partnerIntegrationStatus],
      ['Authorized Integration Since', authorizedSince],
    ].filter(([, value]) => value !== undefined && value !== null && value !== '');
  }, [partnerInformation]);

  return (
    <Accordion variant='main' showBoxShadow>
      <Accordion.Item value='partner-info'>
        <Accordion.Header>
          <Text weight='bold'>Partner Information</Text>
        </Accordion.Header>

        <Accordion.Body>
          {!hasViewAccess ? (
            <Text>No Partner Information available for your role.</Text>
          ) : (
            <>
              <div css={partnerGridStyle}>
                {infoFields.length > 0 ? (
                  infoFields.map(([label, value]) => (
                    <div key={label}>
                      <Text weight='bold'>{label}</Text>
                      <Text>{value || '—'}</Text>
                    </div>
                  ))
                ) : (
                  <Text>No partner metadata available.</Text>
                )}
              </div>

              {partnerInformation?.details && (
                <>
                  <Text weight='bold'>Partner Details</Text>
                  <Text>{partnerInformation.details}</Text>
                </>
              )}
            </>
          )}
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default PartnerInformationAccordion;

const partnerGridStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 40px;
  align-items: start;
  margin-bottom: 40px;
`;
